import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import moment from 'moment';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { Tooltip } from '@material-ui/core';

const styles = theme => ({
  root: {
    position: 'absolute',
    minHeight: '3.5%',
    cursor: 'pointer',
    width: 'calc(100% - 8px)',
  },
  event: {
    display: 'flex',
    flexDirection: 'row',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.secondary.main,
    padding: theme.spacing(0.25, 1, 0.25, 1),
    borderRadius: '7px',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.typography.body1,
  },
  unavailableEvent: {
    color: theme.palette.facet.primaryGray,
    backgroundColor: theme.palette.facet.lightGray2,
    opacity: 0.8,
    cursor: 'default',
    display: 'flex',
    flexFlow: 'column',
  },
  overrideable: {
    cursor: 'pointer',
  },
  selectedEvent: {
    backgroundColor: theme.palette.facet.accentBlue4,
  },
  timeText: {
    fontWeight: 'bold',
  },
  eventBubble: {
    display: 'flex',
    minWidth: '14px',
    height: '14px',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.typography.body1,
  },
});

const CalendarEvent = ({
  className,
  classes,
  startTime,
  endTime,
  windowStart,
  windowEnd,
  onClick,
  isUnavailable,
  isOverrideable,
}) => {
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent({ nodeName: 'Scheduler Calendar Event' });
  const offset30 = 1000 * 60 * 30; // / 12 * (hourEnd - hourStart); // 30 minute offset for alignment purposes
  const offset60 = 1000 * 60 * 60; // / 12 * (hourEnd - hourStart); // 60 minute offset for alignment purposes
  const startMoment = moment(startTime);
  const endMoment = moment(endTime);
  const minStart = moment.max(windowStart, startMoment);
  const maxEnd = moment.min(windowEnd, endMoment);

  const eventPositionStyle = {
    height: `calc(${
      ((maxEnd.valueOf() - minStart.valueOf())
        / (windowEnd.valueOf() - windowStart.valueOf() + offset60))
      * 100
    }% - 3px)`,
    top: `${
      ((minStart.valueOf() - windowStart.valueOf() + offset30)
        / (windowEnd.valueOf() - windowStart.valueOf() + offset60))
      * 100
    }%`,
  };

  const formattedStartTime = startMoment.format('hh:mmA');
  const formattedEndTime = endMoment.format('hh:mmA');

  const handleEventClick = () => {
    logEvent('Clicked Available Event on Scheduler');
    onClick();
  };

  const handleUnavailableEventClick = () => {
    logEvent('Clicked Unavailable Event on Scheduler');
    onClick();
  };

  if (isUnavailable) {
    const overrideText = (
      <div>
        Click to Override
      </div>
    );
    return (
      <Tooltip
        arrow
        placement="top"
        title={`${formattedStartTime} - ${formattedEndTime}`}
      >
        <div
          className={classNames(classes.root, className)}
          style={eventPositionStyle}
          onClick={isOverrideable ? handleUnavailableEventClick : () => {}}
          role="presentation"
          test="scheduler-unavailable-event"
        >
          <div className={classNames({
            [classes.event]: true,
            [classes.unavailableEvent]: true,
            [classes.overrideable]: isOverrideable,
          })}
          >
          Unavailable
            {isOverrideable && overrideText}
          </div>
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      arrow
      placement="top"
      title={`${formattedStartTime} - ${formattedEndTime}`}
    >
      <div
        {...rootNodeProps}
        className={classNames(classes.root, className)}
        role="presentation"
        onClick={handleEventClick}
        style={eventPositionStyle}
        test="scheduler-available-event"
      >
        <div className={classNames(classes.event)}>
        Select
        </div>
      </div>
    </Tooltip>
  );
};

CalendarEvent.defaultProps = {
  className: '',
};

CalendarEvent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  windowStart: PropTypes.object.isRequired,
  windowEnd: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  isUnavailable: PropTypes.bool.isRequired,
  isOverrideable: PropTypes.bool.isRequired,
};

export default withStyles(styles)(CalendarEvent);
